// Pure logic for the job board: the URL query string to filter state and back,
// API parameters, human labels, relative time and facet option merging.
// No React in here.

import { ApplicationStatus, CoverageLevel, FacetOption, JobCoverage, JobFacets, JobFilters, JobSort } from '../types';

/** Jobs per page. The API default; sent explicitly so page counts agree. */
export const PAGE_SIZE = 24;

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

// These mirror WORKPLACE_TYPES, EXPERIENCE_LEVELS, CATEGORIES and JOB_TYPES in
// backend/apps/jobs/models.py. Key order is the order options are listed in.

export const WORKPLACE_LABELS: Record<string, string> = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site',
};

export const LEVEL_LABELS: Record<string, string> = {
  intern: 'Internship',
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior',
  lead: 'Staff / Principal / Lead',
  manager: 'Manager',
  director: 'Director',
  executive: 'Executive',
};

export const CATEGORY_LABELS: Record<string, string> = {
  engineering: 'Software Engineering',
  hardware: 'Hardware & Manufacturing',
  data: 'Data & AI',
  science: 'Science & Research',
  healthcare: 'Healthcare',
  consulting: 'Consulting & Services',
  security: 'Security',
  it: 'IT',
  design: 'Design',
  product: 'Product & Program',
  marketing: 'Marketing',
  sales: 'Sales',
  customer_success: 'Customer Success & Support',
  people: 'People & Recruiting',
  finance: 'Finance',
  legal: 'Legal & Compliance',
  operations: 'Operations',
  other: 'Other',
};

export const JOB_TYPE_LABELS: Record<string, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  internship: 'Internship',
};

export const ATS_LABELS: Record<string, string> = {
  greenhouse: 'Greenhouse',
  lever: 'Lever',
  ashby: 'Ashby',
  workable: 'Workable',
  gem: 'Gem',
  rippling: 'Rippling',
  jazzhr: 'JazzHR',
  recruitee: 'Recruitee',
  jobvite: 'Jobvite',
  breezy: 'Breezy HR',
};

/**
 * The ATS whose public API publishes the application form, so its questions
 * can be read ahead of time. Mirrors PUBLISHING_PLATFORMS in
 * backend/apps/jobs/questions.py (for the last four the form is read from the
 * posting's public page). Lever's is not read ahead of time.
 */
const FORM_PUBLISHING_PLATFORMS = ['greenhouse', 'ashby', 'workable', 'gem', 'rippling', 'jazzhr', 'recruitee', 'jobvite', 'breezy'];

export function publishesApplicationForm(platform: string | null | undefined): boolean {
  return FORM_PUBLISHING_PLATFORMS.includes((platform ?? '').toLowerCase());
}

export const SORT_LABELS: Record<JobSort, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  salary: 'Highest salary',
};

/** Mirrors COVERAGE_LEVELS in backend/apps/jobs/board.py, in display order. */
export const COVERAGE_LABELS: Record<CoverageLevel, string> = {
  ready: 'Ready to apply',
  near: 'Almost ready',
};

export const COVERAGE_LEVELS = Object.keys(COVERAGE_LABELS) as CoverageLevel[];

/** "Ready to apply", or "7 of 9 answered" while required questions are missing. */
export function coverageLabel(coverage: JobCoverage): string {
  return coverage.ready ? COVERAGE_LABELS.ready : `${coverage.answered} of ${coverage.required} answered`;
}

export const AGENCY_LABEL = 'Staffing agency';
export const AGENCY_HINT = 'This listing is from a staffing agency or job marketplace, not the employer itself';

/**
 * The note under the "Hide staffing agencies" switch: how many of the
 * listings matching the other filters it hides, or shows when it is off.
 * `count` is null until the counts have loaded.
 */
export function agencyCaption(hidden: boolean, count: number | null): string {
  if (count === null) {
    return hidden
      ? 'Hides listings from staffing agencies and job marketplaces'
      : 'Showing listings from staffing agencies and job marketplaces';
  }
  if (count === 0) return 'No agency listings match your filters.';
  const amount = count.toLocaleString('en-US');
  return hidden
    ? `Hides ${amount} ${count === 1 ? 'listing' : 'listings'} from staffing agencies and job marketplaces`
    : `Showing ${amount} agency ${count === 1 ? 'listing' : 'listings'}`;
}

/** "Posted within" choices, in days. `null` is "any time". */
export const DEFAULT_POSTED_WITHIN = 1;
// The past 24 hours is the default, so the URL only says when it is something else.
const POSTED_PARAM_ANY = 'any';
export const POSTED_WITHIN_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Past 24 hours' },
  { value: 3, label: 'Past 3 days' },
  { value: 7, label: 'Past 7 days' },
  { value: 30, label: 'Past 30 days' },
];

/** "some_value" -> "Some value", for values this build has no label for. */
export function humanize(value: string): string {
  const text = value.replace(/[_-]+/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : value;
}

let regionNames: { of: (code: string) => string | undefined } | null | undefined;

/** "US" -> "United States". Falls back to the code itself. */
export function countryName(code: string): string {
  const upper = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return upper || code;
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      regionNames = null;
    }
  }
  if (!regionNames) return upper;
  try {
    return regionNames.of(upper) || upper;
  } catch {
    return upper;
  }
}

/** The multi-valued filters; each is also a facet group and a query parameter. */
export type FacetGroup = 'workplace' | 'level' | 'category' | 'job_type' | 'country' | 'ats' | 'company' | 'tech';

export const FACET_GROUPS: FacetGroup[] = [
  'workplace',
  'level',
  'category',
  'job_type',
  'country',
  'ats',
  'company',
  'tech',
];

const GROUP_LABELS: Partial<Record<FacetGroup, Record<string, string>>> = {
  workplace: WORKPLACE_LABELS,
  level: LEVEL_LABELS,
  category: CATEGORY_LABELS,
  job_type: JOB_TYPE_LABELS,
  ats: ATS_LABELS,
};

/** Human label for one value of a filter group. */
export function optionLabel(group: FacetGroup, value: string): string {
  if (group === 'country') return countryName(value);
  // Company and technology names are shown as the API returns them.
  if (group === 'company' || group === 'tech') return value;
  return GROUP_LABELS[group]?.[value] ?? humanize(value);
}

export const workplaceLabel = (value: string): string => optionLabel('workplace', value);
export const levelLabel = (value: string): string => optionLabel('level', value);
export const categoryLabel = (value: string): string => optionLabel('category', value);
export const jobTypeLabel = (value: string): string => optionLabel('job_type', value);
export const atsLabel = (value: string): string => optionLabel('ats', value);

/** Statuses that mean the application went out (mirrors the API's hide_applied). */
const APPLIED_STATUSES: ApplicationStatus[] = [
  'applied',
  'interview_scheduled',
  'interviewed',
  'offer_received',
  'offer_accepted',
  'rejected',
  'withdrawn',
];

export function isAppliedStatus(status: ApplicationStatus | null | undefined): boolean {
  return Boolean(status) && APPLIED_STATUSES.includes(status as ApplicationStatus);
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

/** ISO datetime -> "just now", "5h ago", "3d ago", "2mo ago". '' when absent. */
export function postedAgo(value: string | null | undefined, now: number = Date.now()): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// ---------------------------------------------------------------------------
// Filter state and the URL query string
// ---------------------------------------------------------------------------

export interface JobBoardState {
  q: string;
  location: string;
  workplace: string[];
  level: string[];
  category: string[];
  job_type: string[];
  country: string[];
  ats: string[];
  company: string[];
  tech: string[];
  /** Yearly amount, or null for no minimum. */
  salary_min: number | null;
  has_salary: boolean;
  /** Days, or null for any time. */
  posted_within: number | null;
  hide_applied: boolean;
  saved: boolean;
  /** Leave out staffing agencies and job marketplaces. On unless turned off. */
  hide_agencies: boolean;
  /** Only jobs whose required questions are answered (or nearly); null for all. */
  coverage: CoverageLevel | null;
  sort: JobSort;
  /** 1-based. */
  page: number;
}

export const DEFAULT_BOARD_STATE: JobBoardState = {
  q: '',
  location: '',
  workplace: [],
  level: [],
  category: [],
  job_type: [],
  country: [],
  ats: [],
  company: [],
  tech: [],
  salary_min: null,
  has_salary: false,
  posted_within: DEFAULT_POSTED_WITHIN,
  hide_applied: false,
  saved: false,
  hide_agencies: true,
  coverage: null,
  sort: 'newest',
  page: 1,
};

const SORTS: JobSort[] = ['newest', 'oldest', 'salary'];
const MAX_LIST_VALUES = 40; // the API reads at most this many per filter
const MAX_TEXT = 120;
const MAX_SALARY = 10000000;
const MAX_POSTED_WITHIN = 3650;
const TRUTHY = ['1', 'true', 'yes'];
// Hiding agencies is the default, so the URL only says when they are shown.
const AGENCIES_PARAM = 'agencies';
const AGENCIES_SHOWN = 'show';

/** Trim and collapse whitespace; what a text filter looks like in the URL. */
export function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT);
}

/** Keep the digits of what was typed: "150,000" -> "150000", "abc" -> "". */
export function normalizeAmount(value: string): string {
  const digits = value.replace(/[^\d]/g, '').replace(/^0+/, '');
  if (!digits) return '';
  return String(Math.min(Number(digits), MAX_SALARY));
}

/**
 * A value the API cannot take in a comma-separated filter: the list is split
 * on commas, so a name containing one can never match.
 */
export function isFilterableValue(value: string): boolean {
  return value.trim() !== '' && !value.includes(',');
}

function parseList(raw: string | null, upper = false): string[] {
  if (!raw) return [];
  const out: string[] = [];
  raw.split(',').forEach((part) => {
    const value = upper ? part.trim().toUpperCase() : part.trim();
    if (value && !out.includes(value) && out.length < MAX_LIST_VALUES) out.push(value);
  });
  return out;
}

function parseInteger(raw: string | null, low: number, high: number): number | null {
  if (!raw || !/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());
  return value >= low && value <= high ? value : null;
}

const parseFlag = (raw: string | null): boolean => TRUTHY.includes((raw ?? '').toLowerCase());

/** Absent or malformed: the default window. "any": no limit. */
function parsePostedWithin(raw: string | null): number | null {
  if ((raw ?? '').trim().toLowerCase() === POSTED_PARAM_ANY) return null;
  return parseInteger(raw, 1, MAX_POSTED_WITHIN) ?? DEFAULT_POSTED_WITHIN;
}

/**
 * Read the board state out of a query string. Anything missing or malformed
 * falls back to its default, so any URL yields a usable state.
 */
export function parseBoardQuery(query: string | URLSearchParams): JobBoardState {
  const params = typeof query === 'string' ? new URLSearchParams(query) : query;
  const sort = params.get('sort') as JobSort | null;
  const coverage = params.get('coverage') as CoverageLevel | null;
  return {
    q: normalizeText(params.get('q') ?? ''),
    location: normalizeText(params.get('location') ?? ''),
    workplace: parseList(params.get('workplace')),
    level: parseList(params.get('level')),
    category: parseList(params.get('category')),
    job_type: parseList(params.get('job_type')),
    country: parseList(params.get('country'), true),
    ats: parseList(params.get('ats')),
    company: parseList(params.get('company')),
    tech: parseList(params.get('tech')),
    salary_min: parseInteger(params.get('salary_min'), 1, MAX_SALARY),
    has_salary: parseFlag(params.get('has_salary')),
    posted_within: parsePostedWithin(params.get('posted_within')),
    hide_applied: parseFlag(params.get('hide_applied')),
    saved: parseFlag(params.get('saved')),
    hide_agencies: (params.get(AGENCIES_PARAM) ?? '').toLowerCase() !== AGENCIES_SHOWN,
    coverage: coverage && COVERAGE_LEVELS.includes(coverage) ? coverage : null,
    sort: sort && SORTS.includes(sort) ? sort : 'newest',
    page: parseInteger(params.get('page'), 1, Number.MAX_SAFE_INTEGER) ?? 1,
  };
}

/** [name, value] pairs for everything that differs from the defaults. */
function queryPairs(state: JobBoardState): [string, string][] {
  const pairs: [string, string][] = [];
  if (state.q) pairs.push(['q', state.q]);
  if (state.location) pairs.push(['location', state.location]);
  FACET_GROUPS.forEach((group) => {
    const values = state[group].filter(isFilterableValue);
    if (values.length) pairs.push([group, values.join(',')]);
  });
  if (state.salary_min) pairs.push(['salary_min', String(state.salary_min)]);
  if (state.has_salary) pairs.push(['has_salary', '1']);
  if (!state.posted_within) pairs.push(['posted_within', POSTED_PARAM_ANY]);
  else if (state.posted_within !== DEFAULT_POSTED_WITHIN) pairs.push(['posted_within', String(state.posted_within)]);
  if (state.hide_applied) pairs.push(['hide_applied', '1']);
  if (state.saved) pairs.push(['saved', '1']);
  if (!state.hide_agencies) pairs.push([AGENCIES_PARAM, AGENCIES_SHOWN]);
  if (state.coverage) pairs.push(['coverage', state.coverage]);
  if (state.sort !== 'newest') pairs.push(['sort', state.sort]);
  if (state.page > 1) pairs.push(['page', String(state.page)]);
  return pairs;
}

// Commas stay readable in the address bar ("workplace=remote,hybrid"); they
// are legal in a query string and only ever separate values here.
const encodeValue = (value: string): string => encodeURIComponent(value).replace(/%2C/gi, ',');

/**
 * The query string for a board state, "" or "?a=b&c=d". Defaults are left out
 * and the order is fixed, so equal states always give the same string.
 * `jobId` adds the job whose detail is open over the list.
 */
export function boardSearch(state: JobBoardState, jobId?: string | null): string {
  const pairs = queryPairs(state);
  if (jobId) pairs.push(['job', jobId]);
  if (!pairs.length) return '';
  return `?${pairs.map(([name, value]) => `${name}=${encodeValue(value)}`).join('&')}`;
}

/**
 * What a saved search holds: the board's query without the page or the open
 * job, pairs sorted, no leading "?" (the same form the API keeps, so a saved
 * search and the current search can be compared as strings).
 */
export function savedQuery(state: JobBoardState): string {
  return queryPairs(state)
    .filter(([name]) => name !== 'page' && name !== 'job')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
    .join('&');
}

/** Whether two query strings describe the same search, however they were encoded ("a b" and "a+b" and "a%20b"). */
export function sameQuery(a: string, b: string): boolean {
  const pairs = (query: string) => {
    const entries: [string, string][] = [];
    new URLSearchParams(query.replace(/^\?/, '')).forEach((value, name) => entries.push([name, value]));
    return entries
      .filter(([name]) => name !== 'page' && name !== 'job')
      .sort(([n1, v1], [n2, v2]) => (n1 < n2 ? -1 : n1 > n2 ? 1 : v1 < v2 ? -1 : v1 > v2 ? 1 : 0))
      .map(([name, value]) => `${name}=${value}`)
      .join('&');
  };
  return pairs(a) === pairs(b);
}

/** A name for a search from its filters: "backend, Remote, Past 24 hours". */
export function suggestedSearchName(state: JobBoardState): string {
  const parts = activeFilters(state).map((filter) => filter.label.replace(/^[^:]+:\s*/, ''));
  return parts.join(', ').slice(0, 80);
}

/** Filters only, as the API takes them (no sort or page). Used for facets. */
export function facetApiParams(state: JobBoardState): JobFilters {
  const params: JobFilters = {};
  if (state.q) params.q = state.q;
  if (state.location) params.location = state.location;
  FACET_GROUPS.forEach((group) => {
    const values = state[group].filter(isFilterableValue);
    if (values.length) params[group] = values.join(',');
  });
  if (state.salary_min) params.salary_min = state.salary_min;
  if (state.has_salary) params.has_salary = 1;
  if (state.posted_within) params.posted_within = state.posted_within;
  if (state.hide_applied) params.hide_applied = 1;
  if (state.saved) params.saved = 1;
  if (state.hide_agencies) params.hide_agencies = 1;
  if (state.coverage) params.coverage = state.coverage;
  return params;
}

/** Parameters for one page of `GET /jobs/`. */
export function listApiParams(state: JobBoardState): JobFilters {
  return { ...facetApiParams(state), sort: state.sort, page: state.page, page_size: PAGE_SIZE };
}

/**
 * Signed out, the personal filters do nothing; drop them so the UI agrees.
 * Hiding agencies is not personal and stays as it is.
 */
export function withoutPersonalFilters(state: JobBoardState): JobBoardState {
  return state.hide_applied || state.saved || state.coverage
    ? { ...state, hide_applied: false, saved: false, coverage: null }
    : state;
}

/** Every filter off (hiding agencies, a default with no chip, stays on). Sort is a preference and is kept. */
export function clearedFilters(state: JobBoardState): JobBoardState {
  // "Clear all" removes every chip, the default date window included.
  return { ...DEFAULT_BOARD_STATE, posted_within: null, sort: state.sort };
}

export function totalPages(count: number): number {
  return Math.max(1, Math.ceil(count / PAGE_SIZE));
}

// ---------------------------------------------------------------------------
// Active filter chips
// ---------------------------------------------------------------------------

export interface ActiveFilter {
  key: string;
  label: string;
  /** Apply this to the state to remove just this filter. */
  patch: Partial<JobBoardState>;
}

// Prefixes for groups whose values do not explain themselves on a chip.
const CHIP_PREFIX: Partial<Record<FacetGroup, string>> = {
  job_type: 'Type',
  ats: 'ATS',
  company: 'Company',
};

export function formatAmount(value: number): string {
  return value.toLocaleString('en-US');
}

export function postedWithinLabel(days: number): string {
  const preset = POSTED_WITHIN_OPTIONS.find((option) => option.value === days);
  if (preset) return preset.label;
  return days === 1 ? 'Past day' : `Past ${days} days`;
}

/** One entry per active filter, in the order the filters are listed. */
export function activeFilters(state: JobBoardState): ActiveFilter[] {
  const out: ActiveFilter[] = [];
  if (state.q) out.push({ key: 'q', label: `Search: ${state.q}`, patch: { q: '' } });
  if (state.coverage) {
    out.push({ key: 'coverage', label: COVERAGE_LABELS[state.coverage], patch: { coverage: null } });
  }
  // Hiding agencies is the default and gets no chip; showing them does.
  if (!state.hide_agencies) {
    out.push({ key: 'agencies', label: 'Including staffing agencies', patch: { hide_agencies: true } });
  }
  if (state.saved) out.push({ key: 'saved', label: 'Saved only', patch: { saved: false } });
  if (state.hide_applied) {
    out.push({ key: 'hide_applied', label: 'Hiding applied', patch: { hide_applied: false } });
  }
  const addGroup = (group: FacetGroup) => {
    state[group].forEach((value) => {
      const prefix = CHIP_PREFIX[group];
      const label = optionLabel(group, value);
      out.push({
        key: `${group}:${value}`,
        label: prefix ? `${prefix}: ${label}` : label,
        patch: { [group]: state[group].filter((v) => v !== value) },
      });
    });
  };
  (['workplace', 'level', 'category', 'job_type'] as FacetGroup[]).forEach(addGroup);
  if (state.location) {
    out.push({ key: 'location', label: `Location: ${state.location}`, patch: { location: '' } });
  }
  addGroup('country');
  if (state.posted_within) {
    out.push({ key: 'posted_within', label: postedWithinLabel(state.posted_within), patch: { posted_within: null } });
  }
  if (state.salary_min) {
    out.push({
      key: 'salary_min',
      label: `Salary from ${formatAmount(state.salary_min)} a year`,
      patch: { salary_min: null },
    });
  }
  if (state.has_salary) out.push({ key: 'has_salary', label: 'Salary stated', patch: { has_salary: false } });
  (['tech', 'company', 'ats'] as FacetGroup[]).forEach(addGroup);
  return out;
}

// ---------------------------------------------------------------------------
// Facet options
// ---------------------------------------------------------------------------

export interface FilterOption {
  value: string;
  label: string;
  /** null until the counts have loaded. */
  count: number | null;
  selected: boolean;
}

/** The values this build knows for a group, in display order. */
export function knownValues(group: FacetGroup): string[] {
  const labels = GROUP_LABELS[group];
  return labels ? Object.keys(labels) : [];
}

// Short fixed lists keep their natural order (intern before executive) and
// always show every option; the long ones lead with the biggest counts.
const FIXED_ORDER: FacetGroup[] = ['workplace', 'level', 'job_type', 'ats'];

// The API cuts these groups off at this many rows, most jobs first. A value
// missing from a full list is merely outside the top, not at zero.
const FACET_CAPS: Partial<Record<FacetGroup, number>> = { country: 25, company: 30, tech: 30 };

/**
 * The options to show for a filter group: the facet counts from the API
 * merged with the values this build knows and the ones currently selected.
 * A selected value never drops out, even when its count is zero or the API
 * no longer lists it. Its count is then 0, or null (unknown) when the API's
 * list was cut off before reaching it.
 */
export function mergeFacetOptions(
  group: FacetGroup,
  facet: FacetOption[] | null | undefined,
  selected: string[]
): FilterOption[] {
  const rows = (facet ?? []).filter((row) => row && isFilterableValue(String(row.value)));
  const counts: Record<string, number> = {};
  rows.forEach((row) => {
    counts[row.value] = row.count;
  });
  const loaded = Boolean(facet);
  const known = knownValues(group);
  const cap = FACET_CAPS[group];
  const missing = cap !== undefined && (facet ?? []).length >= cap ? null : 0;

  const order: string[] = [];
  const add = (value: string) => {
    if (value && !order.includes(value)) order.push(value);
  };

  if (FIXED_ORDER.includes(group) || !loaded) known.forEach(add);
  rows.forEach((row) => add(row.value));
  selected.forEach(add);

  return order.map((value) => ({
    value,
    label: optionLabel(group, value),
    count: loaded ? counts[value] ?? missing : null,
    selected: selected.includes(value),
  }));
}

/** Facet rows for a group, tolerating a partial or missing facets payload. */
export function facetRows(facets: JobFacets | null | undefined, group: FacetGroup): FacetOption[] | null {
  const rows = facets?.[group];
  return Array.isArray(rows) ? rows : null;
}

/** Number of active filters in a group of the panel, for its header badge. */
export function countSelected(state: JobBoardState, keys: (keyof JobBoardState)[]): number {
  return keys.reduce((total, key) => {
    const value = state[key];
    if (Array.isArray(value)) return total + value.length;
    if (key === 'sort' || key === 'page') return total;
    // On by default: it is showing agencies that counts as a choice.
    if (key === 'hide_agencies') return total + (value ? 0 : 1);
    return total + (value ? 1 : 0);
  }, 0);
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

/** A URL from a listing, only if it is safe to put in an href (http or https). */
export function externalUrl(url: string | null | undefined): string {
  const value = (url ?? '').trim();
  return /^https?:\/\//i.test(value) ? value : '';
}

/**
 * The board filtered to one company, or '' when the name cannot be filtered
 * on. An agency's own listings only show with agencies included.
 */
export function companyBoardSearch(name: string, isStaffingAgency = false): string {
  if (!isFilterableValue(name)) return '';
  // Everything the company has open, not only what was posted in the default window.
  return boardSearch({ ...DEFAULT_BOARD_STATE, posted_within: null, company: [name.trim()], hide_agencies: !isStaffingAgency });
}
